import * as THREE from "three";
import type { MatLib } from "../world/materials";
import type { LevelData, SpawnPoint } from "../world/level";
import type { Player } from "../player/player";
import type { AudioSys } from "../engine/audio";
import { hitscan, type AABB } from "../engine/collision";

export type Enemy = {
  id: number;
  root: THREE.Group;
  head: THREE.Object3D;
  torso: THREE.Object3D;
  pos: THREE.Vector3;
  yaw: number;
  hp: number;
  state: "patrol" | "combat" | "search" | "dead";
  nextShot: number;
  alert: number;
  navI: number;
  role: SpawnPoint["role"];
  name: string;
  flash: THREE.Mesh;
  weapon: THREE.Group;
  deadT: number;
  lastKnown: THREE.Vector3;
  coverT: number;
  acquire: number;
  marker: THREE.Sprite;
};

const NAMES = [
  "Rook", "Vellum", "Piton", "Haze", "Sable", "Nock", "Quill", "Dredge",
  "Wick", "Bramble", "Cinder", "Lumen", "Grit", "Vesper-6", "Knot", "Ashlar",
  "Fathom", "Brine",
];

function markerSprite() {
  const c = document.createElement("canvas");
  c.width = c.height = 64;
  const g = c.getContext("2d")!;
  g.clearRect(0, 0, 64, 64);
  g.fillStyle = "#ff2a2a";
  g.beginPath();
  g.moveTo(32, 8);
  g.lineTo(54, 48);
  g.lineTo(10, 48);
  g.closePath();
  g.fill();
  g.fillStyle = "#ffe8e0";
  g.beginPath();
  g.moveTo(32, 18);
  g.lineTo(44, 42);
  g.lineTo(20, 42);
  g.closePath();
  g.fill();
  const tex = new THREE.CanvasTexture(c);
  const mat = new THREE.SpriteMaterial({ map: tex, transparent: true, depthTest: true, depthWrite: false, sizeAttenuation: true });
  const s = new THREE.Sprite(mat);
  s.position.set(0, 2.25, 0);
  s.scale.set(0.7, 0.7, 0.7);
  s.renderOrder = 20;
  return s;
}

function buildBody(mats: MatLib) {
  const root = new THREE.Group();
  const box = new THREE.BoxGeometry(1, 1, 1);
  const cyl = new THREE.CylinderGeometry(0.5, 0.5, 1, 8);
  const bodyMat = new THREE.MeshBasicMaterial({ color: 0xe8a04a });
  const darkMat = new THREE.MeshBasicMaterial({ color: 0x1c2228 });
  const vestMat = new THREE.MeshBasicMaterial({ color: 0xff4d2e });
  const legs = new THREE.Mesh(box, darkMat);
  legs.position.set(0, 0.5, 0);
  legs.scale.set(0.42, 1.0, 0.34);
  const torso = new THREE.Mesh(box, bodyMat);
  torso.position.set(0, 1.28, 0);
  torso.scale.set(0.58, 0.68, 0.38);
  const plate = new THREE.Mesh(box, vestMat);
  plate.position.set(0, 1.32, 0.16);
  plate.scale.set(0.5, 0.44, 0.14);
  const head = new THREE.Mesh(cyl, darkMat);
  head.position.set(0, 1.8, 0.02);
  head.scale.set(0.32, 0.32, 0.32);
  const visor = new THREE.Mesh(box, vestMat);
  visor.position.set(0, 1.82, 0.16);
  visor.scale.set(0.26, 0.1, 0.07);
  const strobe = new THREE.Mesh(box, vestMat);
  strobe.position.set(0.22, 1.5, 0.0);
  strobe.scale.set(0.08, 0.08, 0.08);
  const padL = new THREE.Mesh(box, mats.gray);
  padL.position.set(-0.34, 1.42, 0);
  padL.scale.set(0.18, 0.16, 0.3);
  const padR = padL.clone();
  padR.position.x = 0.34;
  const weapon = new THREE.Group();
  const rec = new THREE.Mesh(box, mats.gunDark);
  rec.scale.set(0.06, 0.08, 0.42);
  rec.position.set(0.28, 1.12, 0.28);
  weapon.add(rec);
  const flash = new THREE.Mesh(new THREE.PlaneGeometry(0.22, 0.22), new THREE.MeshBasicMaterial({
    color: 0xffc878,
    transparent: true,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    side: THREE.DoubleSide,
  }));
  flash.position.set(0.28, 1.12, 0.52);
  flash.visible = false;
  weapon.add(flash);
  for (const m of [legs, torso, plate, head, visor, strobe, padL, padR, rec]) {
    m.castShadow = true;
    m.receiveShadow = true;
  }
  const marker = markerSprite();
  root.add(legs, torso, plate, head, visor, strobe, padL, padR, weapon, marker);
  return { root, head, torso, weapon, flash, marker };
}

export class Director {
  enemies: Enemy[] = [];
  group = new THREE.Group();
  frozen = false;
  private tmpO = new THREE.Vector3();
  private tmpD = new THREE.Vector3();
  private tmpT = new THREE.Vector3();
  private losBoxes: AABB[];
  kills = 0;

  get colliders() {
    return this.losBoxes;
  }

  constructor(private level: LevelData, mats: MatLib) {
    this.losBoxes = level.colliders;
    let i = 0;
    for (const s of level.spawns) {
      const body = buildBody(mats);
      body.root.position.copy(s.pos);
      this.group.add(body.root);
      this.enemies.push({
        id: i,
        root: body.root,
        head: body.head,
        torso: body.torso,
        pos: s.pos.clone(),
        yaw: s.yaw,
        hp: 100,
        state: "patrol",
        nextShot: 0,
        alert: 0,
        navI: (i * 3) % Math.max(1, level.nav.length),
        role: s.role,
        name: NAMES[i % NAMES.length],
        flash: body.flash,
        weapon: body.weapon,
        deadT: 0,
        lastKnown: s.pos.clone(),
        coverT: 0,
        acquire: 1.4,
        marker: body.marker,
      });
      i++;
    }
    for (const e of this.enemies) this.eject(e);
  }

  living() {
    return this.enemies.filter((e) => e.state !== "dead");
  }

  hitTest(origin: THREE.Vector3, dir: THREE.Vector3, maxDist: number) {
    let best = maxDist;
    let who: Enemy | null = null;
    let part: "head" | "body" = "body";
    for (const e of this.enemies) {
      if (e.state === "dead") continue;
      const headW = new THREE.Vector3();
      e.head.getWorldPosition(headW);
      const torsoW = new THREE.Vector3();
      e.torso.getWorldPosition(torsoW);
      const headHit = this.raySphere(origin, dir, headW, 0.18, best);
      if (headHit !== null) {
        best = headHit;
        who = e;
        part = "head";
      }
      const bodyHit = this.raySphere(origin, dir, torsoW, 0.38, best);
      if (bodyHit !== null) {
        best = bodyHit;
        who = e;
        part = "body";
      }
    }
    if (!who) return null;
    return { enemy: who, part, dist: best, point: origin.clone().addScaledVector(dir, best) };
  }

  private raySphere(o: THREE.Vector3, d: THREE.Vector3, c: THREE.Vector3, r: number, max: number) {
    const oc = this.tmpT.copy(o).sub(c);
    const b = oc.dot(d);
    const disc = b * b - (oc.lengthSq() - r * r);
    if (disc < 0) return null;
    const t = -b - Math.sqrt(disc);
    if (t < 0 || t > max) return null;
    return t;
  }

  damage(e: Enemy, amount: number, from: THREE.Vector3) {
    if (e.state === "dead") return false;
    e.hp -= amount;
    e.alert = 8;
    e.state = "combat";
    e.lastKnown.copy(from);
    e.root.traverse((o) => {
      const m = o as THREE.Mesh;
      if (m.isMesh && (m.material as THREE.MeshStandardMaterial).emissive) {
        /* flash via scale punch */
      }
    });
    e.root.scale.set(1.04, 0.96, 1.04);
    if (e.hp <= 0) {
      e.state = "dead";
      e.deadT = 0;
      e.flash.visible = false;
      this.kills++;
      return true;
    }
    return false;
  }

  update(dt: number, time: number, player: Player, audio: AudioSys, onEnemyShot: (origin: THREE.Vector3, dir: THREE.Vector3, e: Enemy) => void) {
    const ppos = this.tmpO.set(player.camera.position.x, player.camera.position.y, player.camera.position.z);
    let firing = 0;
    for (const e of this.enemies) {
      if (e.state === "dead") {
        e.deadT += dt;
        e.marker.visible = false;
        const t = Math.min(1, e.deadT / 0.55);
        e.root.rotation.x = t * 1.35;
        e.root.position.y = THREE.MathUtils.lerp(0, 0.15, Math.sin(t * Math.PI));
        if (t > 0.98) e.root.position.y = 0.08;
        continue;
      }
      e.marker.visible = true;
      e.marker.material.opacity = e.state === "combat" ? 1 : 0.55;
      e.root.scale.lerp(new THREE.Vector3(1, 1, 1), 1 - Math.exp(-dt * 12));
      e.flash.visible = false;

      const toP = this.tmpD.copy(ppos).sub(e.pos);
      const dist = toP.length();
      const canSee = this.canSee(e, ppos);

      if (canSee) {
        if (e.state !== "combat") e.acquire = 1.15 + Math.random() * 0.5;
        e.alert = 6;
        e.lastKnown.copy(ppos);
        e.state = "combat";
      } else if (e.alert > 0) {
        e.alert -= dt;
        if (e.state === "combat") e.state = "search";
        if (e.alert <= 0) e.state = "patrol";
      }

      if (e.state === "patrol") {
        const tgt = this.level.nav[e.navI] ?? e.pos;
        this.steer(e, tgt, dt, e.role === "guard" ? 1.15 : 1.7);
        if (e.pos.distanceTo(tgt) < 1.2) e.navI = (e.navI + 1 + (e.id % 3)) % this.level.nav.length;
      } else if (e.state === "search") {
        this.steer(e, e.lastKnown, dt, 2.4);
      } else if (e.state === "combat") {
        e.coverT -= dt;
        e.acquire = Math.max(0, e.acquire - dt);
        const aimYaw = Math.atan2(-(ppos.x - e.pos.x), -(ppos.z - e.pos.z));
        e.yaw = THREE.MathUtils.damp(e.yaw, aimYaw, 8, dt);
        if (dist > 14 || e.coverT > 0) {
          const cover = this.nearestCover(e.pos);
          if (cover) this.steer(e, cover.pos, dt, 3.1);
        } else if (dist < 6) {
          this.steer(e, e.pos.clone().add(e.pos.clone().sub(ppos).setY(0).normalize()), dt, 2.2);
        } else {
          this.steer(e, ppos, dt, 1.4);
        }
        const inRange = dist < 28;
        const canShoot = !this.frozen && canSee && e.acquire <= 0 && time >= e.nextShot && inRange && player.alive && firing < 2;
        if (canShoot) {
          firing++;
          e.nextShot = time + 0.7 + Math.random() * 0.55;
          e.flash.visible = true;
          const origin = new THREE.Vector3();
          e.flash.getWorldPosition(origin);
          const dir = ppos.clone().sub(origin).normalize();
          const miss = 0.05 + dist * 0.0035;
          dir.x += (Math.random() - 0.5) * miss;
          dir.y += (Math.random() - 0.5) * miss * 0.7;
          dir.z += (Math.random() - 0.5) * miss;
          dir.normalize();
          audio.play("ar", { vol: 0.38, x: e.pos.x, y: 1.5, z: e.pos.z, rate: 1.05 });
          onEnemyShot(origin, dir, e);
          if (Math.random() < 0.3) e.coverT = 0.8 + Math.random() * 0.7;
        }
      }

      this.eject(e);
      e.root.position.set(e.pos.x, 0, e.pos.z);
      e.root.rotation.y = e.yaw;
    }
  }

  eject(e: Enemy) {
    const r = 0.55;
    for (let n = 0; n < 12; n++) {
      let inside = false;
      for (const b of this.losBoxes) {
        if (b.max.y - b.min.y < 0.8) continue;
        if (e.pos.x + r < b.min.x || e.pos.x - r > b.max.x) continue;
        if (e.pos.z + r < b.min.z || e.pos.z - r > b.max.z) continue;
        if (1.2 < b.min.y || 0.4 > b.max.y) continue;
        const dl = e.pos.x - (b.min.x - r);
        const dr = b.max.x + r - e.pos.x;
        const db = e.pos.z - (b.min.z - r);
        const df = b.max.z + r - e.pos.z;
        const m = Math.min(dl, dr, db, df);
        if (m === dl) e.pos.x = b.min.x - r - 0.15;
        else if (m === dr) e.pos.x = b.max.x + r + 0.15;
        else if (m === db) e.pos.z = b.min.z - r - 0.15;
        else e.pos.z = b.max.z + r + 0.15;
        inside = true;
        break;
      }
      if (!inside) return;
    }
  }

  private nearestCover(p: THREE.Vector3) {
    let best = Infinity;
    let c = this.level.covers[0];
    for (const cov of this.level.covers) {
      const d = cov.pos.distanceToSquared(p);
      if (d < best) {
        best = d;
        c = cov;
      }
    }
    return c;
  }

  private steer(e: Enemy, target: THREE.Vector3, dt: number, speed: number) {
    const dx = target.x - e.pos.x;
    const dz = target.z - e.pos.z;
    const len = Math.hypot(dx, dz);
    if (len < 0.05) return;
    const nx = dx / len;
    const nz = dz / len;
    const next = this.tmpT.set(e.pos.x + nx * speed * dt, e.pos.y, e.pos.z + nz * speed * dt);
    if (!this.blocked(next)) {
      e.pos.copy(next);
    } else {
      const tryA = this.tmpT.set(e.pos.x + nx * speed * dt, e.pos.y, e.pos.z);
      if (!this.blocked(tryA)) e.pos.x = tryA.x;
      const tryB = this.tmpT.set(e.pos.x, e.pos.y, e.pos.z + nz * speed * dt);
      if (!this.blocked(tryB)) e.pos.z = tryB.z;
    }
    e.yaw = THREE.MathUtils.damp(e.yaw, Math.atan2(-nx, -nz), 6, dt);
  }

  private blocked(p: THREE.Vector3) {
    const r = 0.35;
    for (const b of this.losBoxes) {
      if (p.x + r < b.min.x || p.x - r > b.max.x) continue;
      if (p.z + r < b.min.z || p.z - r > b.max.z) continue;
      if (0.9 < b.min.y || 0.3 > b.max.y) continue;
      if (b.max.y - b.min.y < 0.5) continue;
      return true;
    }
    return false;
  }

  private canSee(e: Enemy, target: THREE.Vector3) {
    const origin = this.tmpT.set(e.pos.x, 1.55, e.pos.z);
    const dir = target.clone().sub(origin);
    const dist = dir.length();
    if (dist > 36) return false;
    dir.multiplyScalar(1 / dist);
    const toYaw = Math.atan2(-dir.x, -dir.z);
    let diff = toYaw - e.yaw;
    while (diff > Math.PI) diff -= Math.PI * 2;
    while (diff < -Math.PI) diff += Math.PI * 2;
    if (Math.abs(diff) > (e.state === "combat" ? 1.35 : 0.85)) return false;
    const hit = hitscan(origin, dir, dist - 0.4, this.losBoxes);
    return !hit;
  }
}
